// HTML Render Layer subcomponent
import type {
  HTMLRenderLayerAPI,
  TransformState,
} from "../../../../types/stage-types.js";

export interface HTMLRenderConfig {
  readonly content?: string;
  readonly enableTestWebsite?: boolean;
  readonly getCurrentScale?: () => number;
  readonly appContext?: any;
  readonly uiState?: any;
}

/**
 * Creates an HTML Render Layer for displaying rendered content
 * @param container - Container element for the layer
 * @param config - Layer configuration
 * @returns HTML Render Layer API
 */
export const createHTMLRenderLayer = (
  container: HTMLElement,
  config: HTMLRenderConfig = {}
): HTMLRenderLayerAPI => {
  console.log("🌐 Creating HTML Render Layer");

  let isDestroyed = false;
  let currentContent = "";
  let unsubscribeTheme: (() => void) | null = null;

  /**
   * Generate test website HTML content
   */
  const getTestWebsiteHTML = (): string => {
    return `
      <div class="test-website">
        <header class="test-hero">
          <h1>Test Landing Page</h1>
          <button class="test-cta-button">Get Started</button>
        </header>
        <section class="test-features">
          <div class="test-feature-card">
            <h3>Feature 1</h3>
            <p>Responsive design that works on all devices</p>
          </div>
          <div class="test-feature-card">
            <h3>Feature 2</h3>
            <p>Modern CSS with beautiful animations</p>
          </div>
          <div class="test-feature-card">
            <h3>Feature 3</h3>
            <p>Optimized performance and fast loading</p>
          </div>
        </section>
        <footer class="test-footer">
          <p>&copy; 2024 Test Company - All rights reserved</p>
          <div class="test-footer-links">
            <a href="#privacy">Privacy</a>
            <a href="#terms">Terms</a>
            <a href="#contact">Contact</a>
          </div>
        </footer>
      </div>
    `;
  };

  /**
   * Set content in the layer
   * @param html - HTML content to display
   */
  const setContent = (html: string): void => {
    if (isDestroyed) return;

    currentContent = html;
    container.innerHTML = html;
  };
  /**
   * Apply theme to the render container only (scoped, not global app)
   */
  const applyScopedTheme = (theme: "light" | "dark"): void => {
    if (isDestroyed) return;
    container.classList.toggle("render-theme--dark", theme === "dark");
    container.classList.toggle("render-theme--light", theme === "light");
  };

  /**
   * Get all HTML elements in the layer
   * @returns Array of HTML elements
   */
  const getElements = (): HTMLElement[] => {
    if (isDestroyed) return [];

    return Array.from(container.querySelectorAll("*")) as HTMLElement[];
  };

  /**
   * Get bounding boxes for all elements
   * @returns Array of bounding box objects
   */
  const getBoundingBoxes = (): Array<{
    x: number;
    y: number;
    width: number;
    height: number;
    element: HTMLElement;
  }> => {
    if (isDestroyed) return [];

    const elements = getElements();
    return elements.map((element) => {
      const rect = element.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      return {
        x: rect.left - containerRect.left,
        y: rect.top - containerRect.top,
        width: rect.width,
        height: rect.height,
        element,
      };
    });
  };

  /**
   * Get dimensions of the main content container for centering calculations
   */
  const getContentDimensions = (): { width: number; height: number } | null => {
    if (isDestroyed) return null;

    // Look for the main content container (e.g., .test-website)
    const contentContainer = container.querySelector(
      ".test-website"
    ) as HTMLElement;
    if (!contentContainer) return null;

    const rect = contentContainer.getBoundingClientRect();
    const currentScale = config.getCurrentScale ? config.getCurrentScale() : 1;

    // Get natural (unscaled) dimensions by dividing by current scale
    const naturalDimensions = {
      width: rect.width / currentScale,
      height: rect.height / currentScale,
    };

    // Debug: log natural dimensions only when they change significantly
    const significantChange =
      Math.abs(naturalDimensions.width - 802) > 5 ||
      Math.abs(naturalDimensions.height - 602) > 5;
    if (significantChange) {
      console.log("📐 Content dimensions changed:", naturalDimensions);
    }

    return naturalDimensions;
  };

  /**
   * Handle transform changes (if needed for this layer)
   * @param transform - New transform state
   */
  const setTransform = (_transform: TransformState): void => {
    if (isDestroyed) return;

    // HTML render layer inherits transform from parent container
    // No additional transform handling needed here
  };

  /**
   * Set layer visibility
   * @param visible - Visibility state
   */
  const setVisible = (visible: boolean): void => {
    if (isDestroyed) return;

    container.style.visibility = visible ? "visible" : "hidden";
  };

  /**
   * Initialize the layer
   */
  const init = (): void => {
    // Set initial content
    if (config.content) {
      setContent(config.content);
    } else if (config.enableTestWebsite !== false) {
      // Enable test website by default
      setContent(getTestWebsiteHTML());
    }

    // Initialize theme from UI state if available
    const themeFromState = config.uiState?.getState?.().theme;
    if (themeFromState) {
      applyScopedTheme(themeFromState);
    }

    // Listen for UI state changes to update theme
    if (config.appContext?.eventBus) {
      const listener = (data: any) => {
        const nextTheme = data?.state?.theme;
        if (nextTheme === "light" || nextTheme === "dark") {
          applyScopedTheme(nextTheme);
        }
      };
      unsubscribeTheme = config.appContext.eventBus.on(
        "ui-state-changed",
        listener
      );
    }
  };

  /**
   * Cleanup resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;
    if (unsubscribeTheme) {
      unsubscribeTheme();
      unsubscribeTheme = null;
    }
    container.innerHTML = "";
  };

  // Initialize layer
  init();

  // Return API
  return {
    setContent,
    getElements,
    getBoundingBoxes,
    getContentDimensions,
    setTransform,
    setVisible,
    destroy,
  };
};
